export type WorkspaceBashNavigation = {
  href: string;
  label: string;
  kind: 'page' | 'file' | 'thread' | 'url';
  threadId: string;
  sourceMessageId: string;
  recipientUserId: string;
  deliveryId: string;
  autoNavigate: boolean;
  reference?: string;
  path?: string;
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function fileHrefWithoutLegacyNodeReference(href: string): string {
  if (!href.startsWith('/') || href.startsWith('//')) return href;
  const url = new URL(href, 'https://app.invalid');
  if (!url.searchParams.has('node-reference')) return href;
  url.searchParams.delete('node-reference');
  return `${url.pathname}${url.search}${url.hash}`;
}

export function isSafeNavigationHref(href: string): boolean {
  if (/^\/(?!\/)/.test(href)) {
    return !/[\\\u0000-\u001f]/.test(href);
  }
  try {
    const url = new URL(href);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      Boolean(url.hostname) &&
      !url.username &&
      !url.password &&
      !/[\\\u0000-\u001f]/.test(href)
    );
  } catch {
    return false;
  }
}

export function readWorkspaceBashNavigation(
  output: unknown
): WorkspaceBashNavigation | null {
  const envelope =
    record(output) && output.type === 'json' && record(output.value)
      ? output.value
      : output;
  const data = record(envelope) && record(envelope.data) ? envelope.data : null;
  if (data?.exitCode !== 0) return null;
  const navigation = data?.navigation;
  if (!record(navigation)) return null;
  const {
    href,
    label,
    kind,
    threadId,
    sourceMessageId,
    recipientUserId,
    deliveryId,
    autoNavigate,
    reference,
    path,
  } = navigation;
  if (
    typeof href !== 'string' ||
    !isSafeNavigationHref(href) ||
    typeof label !== 'string' ||
    !label.trim() ||
    !['page', 'file', 'thread', 'url'].includes(String(kind)) ||
    typeof threadId !== 'string' ||
    !threadId ||
    typeof sourceMessageId !== 'string' ||
    !sourceMessageId ||
    typeof recipientUserId !== 'string' ||
    !recipientUserId ||
    typeof deliveryId !== 'string' ||
    !deliveryId ||
    (kind === 'thread' && (typeof reference !== 'string' || !reference)) ||
    (kind === 'file' && (typeof path !== 'string' || !path))
  ) {
    return null;
  }
  return {
    href: kind === 'file' ? fileHrefWithoutLegacyNodeReference(href) : href,
    label: label.trim(),
    kind: kind as WorkspaceBashNavigation['kind'],
    threadId,
    sourceMessageId,
    recipientUserId,
    deliveryId,
    autoNavigate: autoNavigate === true,
    ...(typeof reference === 'string' && reference ? { reference } : {}),
    ...(typeof path === 'string' && path ? { path } : {}),
  };
}

export function navigationCardIdentity(part: unknown): string | null {
  if (!record(part)) return null;
  if (
    part.type !== 'tool-workspaceBash' &&
    part.type !== 'tool-workspace_bash' &&
    part.type !== 'tool-navigate_ui' &&
    part.type !== 'tool-send_chat_message' &&
    !(
      part.type === 'dynamic-tool' &&
      ['workspaceBash', 'navigate_ui', 'send_chat_message'].includes(
        String(part.toolName)
      )
    )
  ) {
    return null;
  }
  const navigation = readWorkspaceBashNavigation(part.output);
  if (!navigation) return null;
  if (navigation.kind === 'thread' && navigation.reference) {
    return `thread:${navigation.reference}`;
  }
  return `navigation:${navigation.kind}:${navigation.path ?? navigation.href}`;
}
